import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet'; // Import Helmet
import vehicle from "../assets/vehicle.png"
import { useLocationPermission, PERMISSION_STATUS } from "../context/LocationPermissionContext";
import "./ProfileScreen.css";

function ProfileScreen() {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedProfile, setUpdatedProfile] = useState({});
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralLoading, setReferralLoading] = useState(false);
  const [vehiclesCount, setVehiclesCount] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);

  const { permissionStatus, showLocationSettings } = useLocationPermission();

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('user_id');

      if (!token || !userId) {
        setError("Authentication required. Please login again.");
        setLoading(false);
        return;
      }

      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      try {
        const response = await fetch(`https://api.rtoxpert.com/api/users/profile/${userId}`, requestOptions);
        const result = await response.json();

        if (response.ok) {
          setProfileData(result.user);
          setUpdatedProfile(result.user);
        } else {
          setError(result.message || "Failed to fetch profile data");
        }
      } catch (err) {
        setError("An error occurred while fetching profile data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
    fetchVehiclesData();
  }, []);

  const fetchVehiclesData = async () => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    };

    try {
      const response = await fetch(`https://api.rtoxpert.com/api/vehicles/user/${userId}`, requestOptions);
      const result = await response.json();

      if (response.ok && result.vehicles) {
        const activeVehicles = result.vehicles.filter(vehicle => !vehicle.deleted);
        setVehiclesCount(activeVehicles.length);
      }
    } catch (err) {
      console.error("Error fetching vehicles:", err);
    }
  };

  const handleGetReferralCode = async () => {
    setReferralLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    if (!token) {
      setError("Authentication required. Please login again.");
      setReferralLoading(false);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      redirect: "follow"
    };

    try {
      const response = await fetch("https://api.rtoxpert.com/api/users/referral-code/create", requestOptions);
      const result = await response.json();

      if (response.ok || result.referral_code) {
        setReferralCode(result.referral_code);
        setShowReferralPopup(true);
      } else {
        setError(result.message || "Failed to get referral code");
      }
    } catch (err) {
      setError("An error occurred while getting referral code");
      console.error(err);
    } finally {
      setReferralLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedProfile({
      ...updatedProfile,
      [name]: value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    setError(null);
    setUpdateSuccess(false);

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      setError("Authentication required. Please login again.");
      setUpdateLoading(false);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      name: updatedProfile.name,
      email: updatedProfile.email,
      date_of_birth: updatedProfile.date_of_birth,
      city: updatedProfile.city,
      state: updatedProfile.state,
      country: updatedProfile.country,
      profile_picture: updatedProfile.profile_picture
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    try {
      const response = await fetch(`https://api.rtoxpert.com/api/users/profile/${userId}`, requestOptions);
      const result = await response.json();

      if (response.ok) {
        setProfileData(updatedProfile);
        setUpdateSuccess(true);
        setIsEditing(false);
      } else {
        setError(result.message || "Failed to update profile");
      }
    } catch (err) {
      setError("An error occurred while updating profile");
      console.error(err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      setError("Authentication required. Please login again.");
      setDeleteLoading(false);
      setShowDeleteConfirmation(false);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: "DELETE",
      headers: myHeaders,
      redirect: "follow"
    };

    try {
      const response = await fetch(`https://api.rtoxpert.com/api/users/profile/${userId}`, requestOptions);
      const result = await response.text();

      if (response.ok) {
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        localStorage.removeItem('user_type');
        navigate('/', { state: { message: "Your account has been successfully deleted." } });
      } else {
        const errorResult = JSON.parse(result);
        setError(errorResult.message || "Failed to delete account");
        setShowDeleteConfirmation(false);
      }
    } catch (err) {
      setError("An error occurred while deleting your account");
      console.error(err);
      setShowDeleteConfirmation(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setUpdatedProfile(profileData);
    setError(null);
  };

  const copyToClipboard = () => {
    const copyButton = document.querySelector('.copy-button');
    const originalText = copyButton.textContent;

    navigator.clipboard.writeText(referralCode)
      .then(() => {
        copyButton.textContent = "Copied!";
        setTimeout(() => {
          copyButton.textContent = originalText;
        }, 1000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  if (loading) {
    return <div className="loading">Loading profile data...</div>;
  }

  if (error && !profileData) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="profile-screen">
      <Helmet>
        <title>Profile | VAAHANBUDDY - Manage Your Vaahan Services</title>
        <meta name="description" content="Manage your profile and vaahan details with VAAHANBUDDY. Update personal information, add vaahan, and access RTO services easily." />
        <meta name="keywords" content="RTO services, vaahan services, India, user profile, vaahan management, RTO assistance" />
        <meta property="og:title" content="Profile | VAAHANBUDDY" />
        <meta property="og:description" content="Manage your vaahan services and profile details with VAAHANBUDDY in India." />
        <meta property="og:image" content="https://rtoxpert.com/assets/vehicle.png" />
        <meta property="og:url" content="https://rtoxpert.com/profile" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {updateSuccess && (
        <div className="success-message">Profile updated successfully!</div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="profile-header-container">
        <div className="profile-header">
          {!isEditing ? (
            <>
              <div className="left-column">
                <div className="profile-avatar">
                  <img
                    src={profileData?.profile_picture || "https://plus.unsplash.com/premium_photo-1661963258031-211e16a9426a?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8bG9uZG9ufGVufDB8fDB8fHww"}
                    alt="User Avatar"
                  />
                </div>
                <div className="profile-info">
                  <h2>{profileData?.name || "User Name"}</h2>
                  <p>
                    {profileData?.email || "Email not available"}<br />
                    {profileData?.phone_number && `Phone: ${profileData.phone_number}`}<br />
                    {profileData?.city && `${profileData.city}, ${profileData.state}, ${profileData.country}`}
                  </p>
                </div>
              </div>

              <div className="right-column">
                <div className="profile-stats">
                  <div className="stat">
                    <div
                      className="stat-icon"
                      style={{ backgroundImage: `url(${vehicle})` }}
                    ></div>
                    <span className="stat-number">{vehiclesCount} {vehiclesCount === 1 ? "Vaahan" : "Vaahans"}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="edit-profile-form">
              <h2>Edit Profile</h2>
              <form onSubmit={handleUpdateProfile}>
                <div className="form-group">
                  <label htmlFor="profile_picture">Profile Picture URL</label>
                  <input
                    type="text"
                    id="profile_picture"
                    name="profile_picture"
                    value={updatedProfile.profile_picture || ""}
                    onChange={handleInputChange}
                    placeholder="Profile Picture URL"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={updatedProfile.name || ""}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={updatedProfile.email || ""}
                    onChange={handleInputChange}
                    placeholder="Your Email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="date_of_birth">Date of Birth</label>
                  <input
                    type="date"
                    id="date_of_birth"
                    name="date_of_birth"
                    value={updatedProfile.date_of_birth ? updatedProfile.date_of_birth.substring(0, 10) : ""}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={updatedProfile.city || ""}
                      onChange={handleInputChange}
                      placeholder="City"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={updatedProfile.state || ""}
                      onChange={handleInputChange}
                      placeholder="State"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={updatedProfile.country || ""}
                    onChange={handleInputChange}
                    placeholder="Country"
                  />
                </div>

                <div className="form-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancelEdit}
                    disabled={updateLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="save-button"
                    disabled={updateLoading}
                  >
                    {updateLoading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {!isEditing && (
          <button onClick={() => setIsEditing(true)} className="edit-profile-button">
            Edit Profile
          </button>
        )}
      </div>

      <div className="profile-menu">
        <div className="menu-grid">
          <div className="menu-item">
            <div className="menu-icon notification-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Notification</span>
            <div className="toggle-switch">
              <input type="checkbox" id="notification-toggle" defaultChecked />
              <label htmlFor="notification-toggle"></label>
            </div>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={showLocationSettings}
          >
            <span style={{ color: "var(--color-primary)" }}>
              Location Permission
              {permissionStatus === PERMISSION_STATUS.DENIED && (
                <span className="permission-status-badge denied">Denied</span>
              )}
            </span>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/addYourVehicle")}
          >
            <div className="menu-icon add-vehicle-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Add Your Vaahan</span>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={handleGetReferralCode}
          >
            <div className="menu-icon referral-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>
              {referralLoading ? "Loading..." : "Refer a Friend"}
            </span>
            <div className="menu-right-icon"></div>
          </div>

          <div className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/becomePartner")}>
            <div className="menu-icon become-partner-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Become Our Partner</span>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/vehicleNoc")}
          >
            <div className="menu-icon noc-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Vaahan NOC</span>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/vehicleReRegistration")}
          >
            <div className="menu-icon re-registration-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Re-registration</span>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={() => setShowContactModal(true)}
          >
            <div className="menu-icon contact-us-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Contact Us</span>
            <div className="menu-right-icon"></div>
          </div>

          <div className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/terms-conditions")}
          >
            <div className="menu-icon terms-condition-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Terms and Conditions</span>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item"
            style={{ cursor: "pointer" }}
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user_id');
              localStorage.removeItem('user_type');
              navigate('/');
            }}
          >
            <div className="menu-icon logout-icon"></div>
            <span style={{ color: "var(--color-primary)" }}>Logout</span>
            <div className="menu-right-icon"></div>
          </div>

          <div
            className="menu-item delete-account-item"
            style={{ cursor: "pointer" }}
            onClick={() => setShowDeleteConfirmation(true)}
          >
            <div className="menu-icon delete-account-icon"></div>
            <span style={{ color: "#D8153D" }}>Delete Account</span>
            <div className="menu-right-icon"></div>
          </div>
        </div>
      </div>

      <div className="share-section">
        <h3>Share with friends</h3>
        <div className="social-icons">
          <div className="social-icon instagram"></div>
          <a
            href="https://wa.me/919554494415"
            className="social-icon whatsapp"
            title="Contact us on WhatsApp"
            aria-label="Contact us on WhatsApp"
          ></a>
          <a
            href="sms:+919554494415"
            className="social-icon message"
            title="Send us an SMS"
            aria-label="Send us an SMS"
          ></a>
          <div className="social-icon facebook"></div>
        </div>
      </div>

      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Account</h3>
            <p>Are you sure you want to delete your account? This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                className="cancel-button"
                onClick={() => setShowDeleteConfirmation(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="delete-button"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReferralPopup && (
        <div className="modal-overlay">
          <div className="modal-content referral-modal">
            <h3>Your Referral Code</h3>
            <div className="referral-code-container">
              <span className="referral-code">{referralCode}</span>
              <button
                className="copy-button"
                onClick={copyToClipboard}
              >
                Copy
              </button>
            </div>
            <p>Share this code with your friends and they'll get special benefits when they sign up!</p>
            <div className="modal-buttons">
              <button
                className="close-button"
                onClick={() => setShowReferralPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className="modal-overlay">
          <div className="modal-content contact-modal">
            <h3>Contact Us</h3>
            <div className="contact-details">
              <p><strong>Phone:</strong> +91 95544 94415</p>
              <p><strong>Email:</strong> hello@vaahanbuddy.com</p>
              <p><strong>Address:</strong> Shop No - 42 First Floor, GDA Commercial Complex, Buddha Vihar Part-A, Taramandal Gorakhpur, U.P. - 273016</p>
            </div>
            <div className="modal-buttons">
              <button
                className="close-button"
                onClick={() => setShowContactModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileScreen;
